import * as ed from "@noble/ed25519";

/** 72h offline grace past token expiry, per license protocol v1. */
const GRACE_SECONDS = 259200;

/** localhost + private-network IPs: not publicly reachable → free dev mode. */
const DEV_HOSTNAME =
  /^(localhost|127\.0\.0\.1|.+\.localhost|.+\.local|10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)$/;

export class BarqScanLicenseError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "BarqScanLicenseError";
  }
}

export interface ActivateOptions {
  key: string;
  hostname: string;
  licenseServer: string;
  /** Base64-encoded 32-byte Ed25519 public key. */
  publicKey: string;
  storage?: Storage;
  fetchFn?: typeof fetch;
  now?: () => number;
}

interface TokenClaims {
  sub: string;
  org: number;
  domain: string;
  plan: string;
  iat: number;
  exp: number;
  /** Optional symbology whitelist (enterprise); absent = all allowed. */
  symbologies?: string[];
}

export interface LicenseGrant {
  plan: string;
  symbologies?: string[];
}

function b64urlToBytes(s: string): Uint8Array {
  const padded = s.replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

async function verifyToken(
  jwt: string,
  publicKey: string,
  key: string,
  hostname: string,
): Promise<TokenClaims | null> {
  const parts = jwt.split(".");
  if (parts.length !== 3) return null;
  const [h, p, sig] = parts;
  try {
    const ok = await ed.verifyAsync(
      b64urlToBytes(sig),
      new TextEncoder().encode(`${h}.${p}`),
      b64urlToBytes(publicKey.replace(/\+/g, "-").replace(/\//g, "_")),
    );
    if (!ok) return null;
    const claims = JSON.parse(
      new TextDecoder().decode(b64urlToBytes(p)),
    ) as TokenClaims;
    if (claims.sub !== key) return null;
    if (claims.domain !== hostname.toLowerCase()) return null;
    return claims;
  } catch {
    return null;
  }
}

async function fetchToken(opts: Required<Pick<ActivateOptions, "key" | "hostname" | "licenseServer" | "fetchFn">>): Promise<string> {
  // Invoke through a local binding: browsers throw "Illegal invocation" when the
  // global fetch is called as a method of a plain object.
  const request = opts.fetchFn;
  let res: Response;
  try {
    res = await request(`${opts.licenseServer}/api/v1/license/activate`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ key: opts.key, domain: opts.hostname.toLowerCase() }),
    });
  } catch {
    throw new BarqScanLicenseError(
      "activation_failed",
      "Could not reach the BarqScan license server.",
    );
  }
  let body: { token?: string; error?: { code?: string; message?: string } };
  try {
    body = await res.json();
  } catch {
    body = {};
  }
  if (!res.ok || !body.token) {
    throw new BarqScanLicenseError(
      body.error?.code ?? "activation_failed",
      body.error?.message ?? `License activation failed (HTTP ${res.status}).`,
    );
  }
  return body.token;
}

function grantOf(claims: TokenClaims): LicenseGrant {
  return claims.symbologies
    ? { plan: claims.plan, symbologies: claims.symbologies }
    : { plan: claims.plan };
}

export async function activateLicense(opts: ActivateOptions): Promise<LicenseGrant> {
  const {
    key,
    hostname,
    licenseServer,
    publicKey,
    storage,
    fetchFn = (...args: Parameters<typeof fetch>) => fetch(...args),
    now = () => Math.floor(Date.now() / 1000),
  } = opts;

  if (DEV_HOSTNAME.test(hostname.toLowerCase())) {
    return { plan: "dev" };
  }

  const cacheKey = `barqscan:token:${key}`;
  const cached = storage?.getItem(cacheKey);
  if (cached) {
    const claims = await verifyToken(cached, publicKey, key, hostname);
    if (claims) {
      const ts = now();
      if (ts < claims.exp) {
        return grantOf(claims);
      }
      if (ts < claims.exp + GRACE_SECONDS) {
        // Grace window: keep scanning, refresh in the background.
        void fetchToken({ key, hostname, licenseServer, fetchFn })
          .then(async (jwt) => {
            if (await verifyToken(jwt, publicKey, key, hostname)) {
              storage?.setItem(cacheKey, jwt);
            }
          })
          .catch(() => {
            // Still inside grace; next load retries.
          });
        return grantOf(claims);
      }
    }
  }

  const jwt = await fetchToken({ key, hostname, licenseServer, fetchFn });
  const claims = await verifyToken(jwt, publicKey, key, hostname);
  if (!claims) {
    throw new BarqScanLicenseError(
      "invalid_token",
      "The license server returned a token that failed verification.",
    );
  }
  storage?.setItem(cacheKey, jwt);
  return grantOf(claims);
}
